import React, { useEffect, useState, useCallback } from "react";
import styled, { keyframes } from "styled-components";

import { ReactComponent as BrainMagnify } from "assets/brain-magnify.svg";
import { ReactComponent as BrainHealthIndex } from "assets/brainhealthindex.svg";
import { ReactComponent as BHITree } from "assets/bhi-tree.svg";
import { color, fontFamily, fontSize, spacingDefaults } from "style/constants";

const SLIDE_INTERVAL_MS = 4000;

const slides = [
  {
    Illustration: BrainMagnify,
    label: "Analyzing your responses"
  },
  {
    Illustration: BrainHealthIndex,
    label: "Calculating your scores"
  },
  {
    Illustration: BHITree,
    label: "Preparing your results"
  }
];

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 356px;
  margin: 4rem auto;
`;

const IllustrationWrapper = styled.div`
  width: 180px;
  height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${spacingDefaults.normal};

  svg {
    max-width: 100%;
    max-height: 100%;
  }

  &.fade-enter {
    animation: ${fadeIn} 0.5s ease forwards;
  }
`;

const Title = styled.h1`
  margin: 0 0 ${spacingDefaults.xsmall};
  color: ${color.BLACK};
  font-family: ${fontFamily.secondary};
  font-size: ${fontSize.large};
`;

const SlideLabel = styled.p`
  margin: 0 0 ${spacingDefaults.normal};
  color: ${color.LIGHTGRAY};
  font-family: ${fontFamily.primary};
  font-size: ${fontSize.normal};
`;

const DotsContainer = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: ${spacingDefaults.normal};
`;

const Dot = styled.button<{ active: boolean }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: none;
  padding: 0;
  cursor: pointer;
  background-color: ${({ active }) => (active ? color.BLUE : color.GRAY)};
  transition: background-color 0.3s ease;
`;

interface ScoringInProgressProps {
  children?: React.ReactNode;
}

const ScoringInProgress: React.FC<ScoringInProgressProps> = ({ children }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [animKey, setAnimKey] = useState(0);

  const goToSlide = useCallback((index: number) => {
    setCurrentIndex(index);
    setAnimKey(prev => prev + 1);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      goToSlide((currentIndex + 1) % slides.length);
    }, SLIDE_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [currentIndex, goToSlide]);

  const { Illustration, label } = slides[currentIndex];

  return (
    <Container>
      <IllustrationWrapper key={animKey} className="fade-enter">
        <Illustration aria-hidden="true" />
      </IllustrationWrapper>

      <Title>Scoring in Progress</Title>
      <SlideLabel>{label}</SlideLabel>

      <DotsContainer>
        {slides.map((_, i) => (
          <Dot
            key={i}
            active={i === currentIndex}
            onClick={() => goToSlide(i)}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </DotsContainer>

      {children}
    </Container>
  );
};

export default ScoringInProgress;
